import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/ui';
import { ROUTES } from '@/config/constants';
import { CommunityDirectory } from '@/features/communities/components/CommunityDirectory';
import { CreateCommunityModal } from '@/features/communities/components/CreateCommunityModal';

/**
 * Home hero — ported 1:1 from the OG `renderMarketingPitch` (js/app.js:2344) +
 * the hero block in `renderHome` (js/app.js:1364): logo absolutely positioned
 * left of the theme-filled WINR wordmark, headline + sub, the Learn/Create CTAs,
 * and the "Explore Featured Communities" label above the marquee.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Blur the themed backdrop behind the home page, matching the Communities
          hub's frosted-glass treatment. The marquee + hero sit on top of it. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-[linear-gradient(180deg,rgba(10,10,14,0.45),rgba(10,10,14,0.6))] backdrop-blur-[22px] backdrop-saturate-[1.3]"
      />
      <Container className="pb-1">
        <div className="relative z-10 pb-0 pt-11 text-center">
          <div className="relative inline-flex items-center justify-center">
            {/* WINR wordmark — the active theme image (--hero-bg) is clipped into the
                letters and pans inside them; falls back to an electric-blue gradient. */}
            <div className="relative inline-block animate-hero-wordmark bg-clip-text pr-[14px] text-[68px] font-black leading-none tracking-[-5px] text-transparent [-webkit-text-fill-color:transparent] [-webkit-text-stroke:3px_#fff] [background-image:var(--hero-bg,linear-gradient(135deg,#8fd2ff_0%,#54b9fe_50%,#1e6fb0_100%))] [background-position:0%_50%] [background-size:200%_200%] [font-family:'Arial_Black',Impact,sans-serif] min-[421px]:text-[96px] md:text-[150px] md:tracking-[-9px]">
              WINR
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto mb-10 mt-6 max-w-[950px] text-center">
          <h3 className="mb-4 whitespace-normal text-center text-[32px] font-black leading-[1.1] tracking-[-1.5px] text-white md:whitespace-nowrap md:text-5xl">
            The Home for Every Community.
          </h3>
          <p className="mx-auto max-w-[680px] text-center text-[15px] leading-[1.6] text-white/70 md:text-lg">
            Launch a premium community, educate your audience, and monetize your passion all in one
            place.
          </p>

          <div className="mb-0 mt-6 flex flex-row flex-wrap items-center justify-center gap-4">
            <button
              className="group inline-flex cursor-pointer items-center gap-2 text-sm font-black tracking-[-0.2px] text-white/82 transition-colors duration-300 ease-cinematic hover:text-accent"
              onClick={() => navigate(ROUTES.about)}
            >
              <span>Learn more about WINR</span>
              <svg
                className="ml-1 text-white/50 transition-[transform,color] duration-300 ease-cinematic group-hover:translate-x-1 group-hover:text-accent"
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            <button
              className="group relative inline-flex cursor-pointer items-center gap-[11px] overflow-hidden rounded-md border border-white/70 bg-gradient-to-b from-white to-[#e9e9ee] px-[22px] py-[13px] text-[15px] font-bold tracking-[-0.25px] text-[#0a0a0f] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_2px_6px_rgba(0,0,0,0.2),0_16px_36px_-12px_rgba(0,0,0,0.6)] transition-[transform,box-shadow,filter] duration-[250ms] ease-cinematic after:pointer-events-none after:absolute after:left-[-60%] after:top-0 after:h-full after:w-[45%] after:bg-[linear-gradient(100deg,transparent,rgba(255,255,255,0.6),transparent)] after:transition-[left] after:duration-[600ms] after:ease-cinematic after:content-[''] after:[transform:skewX(-18deg)] hover:-translate-y-0.5 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_4px_10px_rgba(0,0,0,0.22),0_24px_50px_-14px_rgba(0,0,0,0.7)] hover:brightness-[1.02] active:translate-y-0 active:brightness-[0.98] group-hover:after:left-[130%]"
              onClick={() => setCreateOpen(true)}
            >
              <span className="leading-none">Create Your Community</span>
              <svg
                className="animate-arrow-blue-pulse text-[#3a3a44] opacity-[0.85] transition-[transform,color,filter,opacity] duration-[250ms] ease-cinematic group-hover:translate-x-1 group-hover:text-[#0a84ff] group-hover:opacity-100 group-hover:[animation:none] group-hover:[filter:drop-shadow(0_0_8px_#0a84ff)]"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>

      </Container>

      <CommunityDirectory />
      <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
